import type {
  ClientDetails,
  ReportContentBlock,
  ReportSection,
  SectionSixRecommendationCandidate,
  SectionTenAssessmentV2,
  SectionTenClientDraftV2,
  SectionTenEvidenceStatus,
  SectionTenRecommendationDisposition,
  SectionTenRecommendationHistoryEntry,
  SectionTenRecommendationV2,
  SectionTenSourceReference,
  SectionTenV2Priority,
  SectionTenWorkAreaId,
  SectionTenWorkAreaV2,
} from "./domain";
import { normaliseGeneratedReportText } from "./report-text";

const now=()=>new Date().toISOString();

export const SECTION_TEN_WORK_AREAS:Array<{id:SectionTenWorkAreaId;title:string;defaultSummary:string}>=[
  {id:"visibility-foundations",title:"Correct main visibility foundations",defaultSummary:"Correct the core business information and local visibility foundations before measuring progress again."},
  {id:"website-positioning",title:"Improve website and positioning",defaultSummary:"Make the website and wider positioning clearer, more specific and better aligned with the confirmed commercial priorities."},
  {id:"authority-monitoring",title:"Strengthen authority and monitor progress",defaultSummary:"Build relevant corroboration, then monitor dated prompt evidence to see whether visibility improves."},
];

const sectionArea=(sectionId:string):SectionTenWorkAreaId=>{
  if(["maps-profile","commercial-gaps"].includes(sectionId))return"visibility-foundations";
  if(["website-review","positioning"].includes(sectionId))return"website-positioning";
  return"authority-monitoring";
};

const sectionPriority=(sectionId:string):SectionTenV2Priority=>{
  if(["maps-profile","website-review","commercial-gaps"].includes(sectionId))return"Essential foundation";
  if(sectionId==="positioning")return"High priority";
  if(["competitors","authority-gaps"].includes(sectionId))return"Useful improvement";
  return"Later opportunity";
};

const shortText=(value:string,max=220)=>{
  const clean=value.replace(/\s+/g," ").trim();
  if(clean.length<=max)return clean;
  const clipped=clean.slice(0,max);
  const sentence=clipped.lastIndexOf(". ");
  return`${clipped.slice(0,sentence>80?sentence+1:clipped.lastIndexOf(" "))}…`;
};

export const isSectionTenActionableBlock=(sectionId:string,title:string)=>{
  const value=title.toLowerCase();
  if(sectionId==="maps-profile")return/needs attention|recommended google business profile checks and changes/.test(value);
  if(sectionId==="website-review")return/technical findings reported by ayzeo|recommended website actions/.test(value);
  if(sectionId==="authority-gaps")return/opportunities to strengthen authority|further research|required/.test(value);
  if(sectionId==="positioning")return/what needs to happen next/.test(value);
  return false;
};

export const sectionTenActionableSourceBlocks=(section:ReportSection,eligible?:ReadonlySet<string>)=>
  (!eligible||eligible.has(section.id))
    ?(section.blocks??[]).filter(block=>block.clientVisible&&block.content.trim()&&isSectionTenActionableBlock(section.id,block.title??""))
    :[];

const currentBlocks=(section:ReportSection,eligible:ReadonlySet<string>)=>
  eligible.has(section.id)
    ? sectionTenActionableSourceBlocks(section,eligible)
    : [];

function sourceReference(section:ReportSection,block:ReportContentBlock,current:boolean):SectionTenSourceReference{
  return{sectionId:section.id,blockId:block.id,label:`Section ${section.order}: ${block.title||section.heading}`,current};
}

const actionTitle=(section:ReportSection,block:ReportContentBlock)=>{
  const title=(block.title||section.heading).replace(/^(What|Why|Where|How)\s+/i,"").replace(/[?.]$/,"").trim();
  const prefix=section.id==="maps-profile"?"Review":section.id==="website-review"?"Improve":section.id==="positioning"?"Clarify":section.id==="authority-gaps"?"Strengthen":section.id==="competitors"?"Review":section.id==="prompt-testing"||section.id==="overall-findings"?"Retest":"Address";
  return`${prefix} ${title.charAt(0).toLowerCase()}${title.slice(1)}`;
};

function fromBlock(section:ReportSection,block:ReportContentBlock,current:boolean):SectionTenRecommendationV2{
  const timestamp=now();
  return{
    id:`section10-v2-${section.id}-${block.id}`,
    title:actionTitle(section,block),
    clientSummary:shortText(block.content),
    internalDetail:block.content.trim(),
    workAreaId:sectionArea(section.id),
    priority:sectionPriority(section.id),
    evidenceStatus:current?"Confirmed current evidence":"Source no longer current",
    disposition:current?"Include":"Exclude",
    sourceReferences:[sourceReference(section,block,current)],
    dependencyIds:[],
    updatedAt:timestamp,
  };
}

function candidateArea(candidate:SectionSixRecommendationCandidate):SectionTenWorkAreaId{
  const haystack=`${candidate.deliveryArea} ${candidate.title}`.toLowerCase();
  if(/website|position|service page|about/.test(haystack))return"website-positioning";
  if(/authority|citation|content|retest|monitor/.test(haystack))return"authority-monitoring";
  return"visibility-foundations";
}

function fromCandidate(candidate:SectionSixRecommendationCandidate,current:boolean):SectionTenRecommendationV2{
  const handover:Record<SectionSixRecommendationCandidate["handoverState"],SectionTenRecommendationDisposition>={
    "Include in Section 10":"Include",
    "Provisional":"Defer",
    "Not proceeding":"Exclude",
  };
  return{
    id:`section10-v2-candidate-${candidate.id}`,
    title:candidate.title,
    clientSummary:shortText(candidate.description),
    internalDetail:candidate.description,
    workAreaId:candidateArea(candidate),
    priority:candidate.suggestedPriority==="Immediate"?"Essential foundation":candidate.suggestedPriority==="High"?"High priority":candidate.suggestedPriority==="Medium"?"Useful improvement":"Later opportunity",
    evidenceStatus:current?(candidate.handoverState==="Provisional"?"Provisional":"Confirmed current evidence"):"Source no longer current",
    disposition:current?handover[candidate.handoverState]:"Exclude",
    sourceReferences:[{sectionId:"commercial-gaps",candidateId:candidate.id,label:"Section 6 recommendation handover",current}],
    dependencyIds:[],
    sourceCandidateId:candidate.id,
    updatedAt:now(),
  };
}

const recommendationKey=(item:SectionTenRecommendationV2)=>
  `${item.workAreaId}:${item.title.toLowerCase().replace(/[^a-z0-9]+/g," ").trim()}`;

function deduplicate(items:SectionTenRecommendationV2[]){
  const kept=new Map<string,SectionTenRecommendationV2>();
  for(const item of items){
    const key=recommendationKey(item),existing=kept.get(key);
    if(!existing){kept.set(key,item);continue}
    kept.set(key,{
      ...existing,
      internalDetail:[existing.internalDetail,item.internalDetail].filter((value,index,all)=>all.indexOf(value)===index).join("\n\n"),
      sourceReferences:[...existing.sourceReferences,...item.sourceReferences].filter((value,index,all)=>all.findIndex(candidate=>candidate.sectionId===value.sectionId&&candidate.blockId===value.blockId&&candidate.candidateId===value.candidateId)===index),
      disposition:existing.disposition==="Include"||item.disposition==="Include"?"Include":existing.disposition,
    });
  }
  return[...kept.values()];
}

function isGenericTechnicalAction(item:SectionTenRecommendationV2){
  return item.workAreaId==="website-positioning"&&/ai-plugin\.json|llms\.txt|schema|structured data/i.test(`${item.title} ${item.clientSummary}`)&&item.sourceReferences.some(ref=>ref.sectionId==="website-review");
}

export function prepareSectionTenV2Assessment(sections:ReportSection[],details:ClientDetails|undefined,eligibleSourceIds:ReadonlySet<string>,previous?:SectionTenAssessmentV2):SectionTenAssessmentV2{
  const sourceSections=sections.filter(section=>section.id!=="work-programme");
  const generated=sourceSections.flatMap(section=>currentBlocks(section,eligibleSourceIds).map(block=>fromBlock(section,block,true)));
  const sectionSix=sourceSections.find(section=>section.id==="commercial-gaps");
  const candidates=(sectionSix?.sectionSixWorkflow?.recommendationCandidates??[]).map(candidate=>fromCandidate(candidate,eligibleSourceIds.has("commercial-gaps")));
  const recommendations=deduplicate([...generated,...candidates]).map(item=>isGenericTechnicalAction(item)?{...item,evidenceStatus:"Requires investigation" as const,disposition:"Exclude" as const}:item);
  const previousById=new Map(previous?.recommendations.map(item=>[item.id,item])??[]);
  const mergedCurrent=recommendations.map(item=>{
    const saved=previousById.get(item.id);
    if(!saved)return item;
    return{...item,clientSummary:saved.clientSummary,clientSummaryEditedBySteve:saved.clientSummaryEditedBySteve,history:saved.history,priority:saved.priority,evidenceStatus:item.evidenceStatus==="Source no longer current"?item.evidenceStatus:saved.evidenceStatus,disposition:item.evidenceStatus==="Source no longer current"?"Exclude":saved.disposition,dependencyIds:saved.dependencyIds,mergedIntoId:saved.mergedIntoId,updatedAt:saved.updatedAt};
  });
  const currentIds=new Set(mergedCurrent.map(item=>item.id));
  const retired=(previous?.recommendations??[]).filter(item=>!currentIds.has(item.id)).map(item=>{
    const changed=item.evidenceStatus!=="Source no longer current"||item.disposition!=="Exclude";
    return{...item,evidenceStatus:"Source no longer current" as const,disposition:"Exclude" as const,sourceReferences:item.sourceReferences.map(source=>({...source,current:false})),history:changed?[...(item.history??[]),{at:now(),by:"System",event:"Source retired" as const,from:item.evidenceStatus,to:"Source no longer current"}]:item.history};
  });
  const merged=[...mergedCurrent,...retired];
  const workAreas:SectionTenWorkAreaV2[]=SECTION_TEN_WORK_AREAS.map(area=>{
    const saved=previous?.workAreas.find(item=>item.id===area.id);
    return{...area,summary:saved?.summaryEditedBySteve?saved.summary:area.defaultSummary,summaryEditedBySteve:saved?.summaryEditedBySteve??false,recommendationIds:merged.filter(item=>item.workAreaId===area.id).map(item=>item.id)};
  });
  const priorities=[details?.commercialPriorities,details?.priorityServicesToGrow,details?.priorityCustomerGroups,details?.geographicPriorities].filter(Boolean).join(" ");
  return{
    version:2,
    overview:normaliseGeneratedReportText(priorities?`The recommended programme connects the confirmed audit findings with the recorded commercial priorities. ${shortText(priorities,260)}`:"The recommended programme starts with the main visibility foundations, then improves the website and positioning before strengthening authority and monitoring progress."),
    workAreas,
    recommendations:merged,
    deliveryOptions:"The client may implement the work internally, use an existing supplier or another specialist, or ask Magnum Opus Studio UK for a separate proposal. The audit does not include implementation.",
    nextDecision:"Agree which immediate recommendations to take forward, who will own them and whether a coordinated foundation project would be useful.",
    evidenceLimitations:"Recommendations are based on the current, dated evidence and confirmed audit findings. Sources that are no longer current remain internal. Results can change and no action guarantees visibility, recommendations, enquiries or revenue.",
    capacityInformation:"Unavailable",
    exclusionInformation:"Unavailable",
    internalNotes:"Capacity and exclusion information is unavailable for this development audit and does not block review.",
    updatedAt:now(),
    updatedBy:"Demo Operator",
  };
}

export const sectionTenRecommendationIsClientEligible=(item:SectionTenRecommendationV2)=>
  item.disposition==="Include"&&item.evidenceStatus==="Confirmed current evidence"&&Boolean(item.clientSummary.trim());

function clientPoints(area:SectionTenWorkAreaV2,recommendations:SectionTenRecommendationV2[]){
  const ordered=area.recommendationIds.map(id=>recommendations.find(item=>item.id===id)).filter((item):item is SectionTenRecommendationV2=>Boolean(item)&&sectionTenRecommendationIsClientEligible(item!));
  const limit=ordered.length>3&&ordered.slice(3,5).some(item=>item.priority==="Essential foundation"||item.priority==="High priority")?Math.min(5,ordered.length):Math.min(3,ordered.length);
  return ordered.slice(0,limit).map(item=>normaliseGeneratedReportText(item.clientSummary));
}

export function prepareSectionTenV2ClientDraft(assessment:SectionTenAssessmentV2,confirmedAt:string,confirmationId?:string):SectionTenClientDraftV2{
  return{
    version:2,
    overview:normaliseGeneratedReportText(assessment.overview),
    workAreas:assessment.workAreas.map(area=>({id:area.id,title:area.title,summary:normaliseGeneratedReportText(area.summary),points:clientPoints(area,assessment.recommendations)})),
    deliveryOptions:normaliseGeneratedReportText(assessment.deliveryOptions),
    nextDecision:normaliseGeneratedReportText(assessment.nextDecision),
    evidenceLimitations:normaliseGeneratedReportText(assessment.evidenceLimitations),
    generatedAt:now(),
    sourceAssessmentConfirmedAt:confirmedAt,
    sourceAssessmentConfirmationId:confirmationId,
  };
}

export function updateSectionTenRecommendation(assessment:SectionTenAssessmentV2,itemId:string,changes:Partial<SectionTenRecommendationV2>):SectionTenAssessmentV2{
  const timestamp=now(),current=assessment.recommendations.find(item=>item.id===itemId);
  if(!current)return assessment;
  const entries:SectionTenRecommendationHistoryEntry[]=[];
  const record=(event:SectionTenRecommendationHistoryEntry["event"],from:string|undefined,to:string|undefined)=>{if(from!==to)entries.push({at:timestamp,by:"Demo Operator",event,from,to})};
  if(changes.disposition)record("Decision changed",current.disposition,changes.disposition);
  if(changes.evidenceStatus)record("Evidence status changed",current.evidenceStatus,changes.evidenceStatus);
  if(changes.workAreaId)record("Work area changed",current.workAreaId,changes.workAreaId);
  const wordingChanged=changes.clientSummary!==undefined&&changes.clientSummary!==current.clientSummary;
  const recommendations=assessment.recommendations.map(item=>item.id===itemId?{...item,...changes,id:item.id,sourceReferences:item.sourceReferences,sourceCandidateId:item.sourceCandidateId,clientSummaryEditedBySteve:wordingChanged?true:item.clientSummaryEditedBySteve,history:entries.length?[...(item.history??[]),...entries]:item.history,updatedAt:timestamp}:item);
  let workAreas=assessment.workAreas;
  if(changes.workAreaId&&changes.workAreaId!==current.workAreaId){
    workAreas=workAreas.map(area=>area.id===current.workAreaId?{...area,recommendationIds:area.recommendationIds.filter(id=>id!==itemId)}:area.id===changes.workAreaId?{...area,recommendationIds:[...area.recommendationIds.filter(id=>id!==itemId),itemId]}:area);
  }
  return{...assessment,recommendations,workAreas,updatedAt:timestamp,updatedBy:"Demo Operator"};
}

export function moveSectionTenRecommendation(assessment:SectionTenAssessmentV2,areaId:SectionTenWorkAreaId,itemId:string,direction:-1|1):SectionTenAssessmentV2{
  const area=assessment.workAreas.find(item=>item.id===areaId);
  if(!area)return assessment;
  const index=area.recommendationIds.indexOf(itemId),target=index+direction;
  if(index<0||target<0||target>=area.recommendationIds.length)return assessment;
  const ordered=[...area.recommendationIds];[ordered[index],ordered[target]]=[ordered[target],ordered[index]];
  return{...assessment,workAreas:assessment.workAreas.map(item=>item.id===areaId?{...item,recommendationIds:ordered}:item),updatedAt:now(),updatedBy:"Demo Operator"};
}

export function sectionTenCommercialBlockers(assessment:SectionTenAssessmentV2|undefined,draft?:SectionTenClientDraftV2){
  if(!assessment)return["Confirm the work-programme assessment."];
  const blockers:string[]=[];
  const expected=SECTION_TEN_WORK_AREAS.map(item=>item.id),actual=assessment.workAreas.map(item=>item.id);
  if(actual.length!==expected.length||actual.some((id,index)=>id!==expected[index])||new Set(actual).size!==actual.length)blockers.push("Restore the three work areas to their standard order.");
  const recommendationIds=new Set(assessment.recommendations.map(item=>item.id));
  const orderedIds=assessment.workAreas.flatMap(area=>area.recommendationIds);
  if(orderedIds.length!==assessment.recommendations.length||new Set(orderedIds).size!==orderedIds.length)blockers.push("Every recommendation must appear once in a work area.");
  if(assessment.recommendations.some(item=>!["Include","Defer","Exclude"].includes(item.disposition)))blockers.push("Choose Include, Defer or Exclude for every recommendation.");
  if(assessment.recommendations.some(item=>!["Confirmed current evidence","Requires investigation","Provisional","Insufficient evidence","Unavailable","Source no longer current"].includes(item.evidenceStatus)))blockers.push("Review every recommendation's evidence status.");
  for(const area of assessment.workAreas){
    const ordered=area.recommendationIds.filter(id=>recommendationIds.has(id));
    if(ordered.length!==area.recommendationIds.length||new Set(ordered).size!==ordered.length)blockers.push(`${area.title} has an invalid recommendation order.`);
    const items=ordered.map(id=>assessment.recommendations.find(item=>item.id===id)!).filter(Boolean);
    const included=items.filter(item=>item.disposition==="Include");
    if(!included.length)blockers.push(`${area.title} has no included recommendation.`);
    if(included.length>5)blockers.push(`${area.title} has more than five included recommendations.`);
    if(included.length>3&&!included.slice(3,5).some(item=>item.priority==="Essential foundation"||item.priority==="High priority"))blockers.push(`${area.title} should keep three client points unless an additional recommendation is materially important.`);
    if(included.some(item=>item.evidenceStatus!=="Confirmed current evidence"))blockers.push(`${area.title} includes a recommendation whose evidence still needs review.`);
    if(included.some(item=>!item.clientSummary.trim()))blockers.push(`${area.title} includes a recommendation without client wording.`);
  }
  if(draft){
    if(draft.workAreas.map(item=>item.id).some((id,index)=>id!==expected[index]))blockers.push("The client draft work areas are out of order.");
    if(!draft.overview.trim())blockers.push("Add the client-facing programme overview.");
    if(draft.workAreas.some(area=>!area.summary.trim()))blockers.push("Every work area needs a client-facing summary.");
    if(draft.workAreas.some(area=>area.points.length<1))blockers.push("Every work area needs at least one eligible client point.");
    if(draft.workAreas.some(area=>area.points.length>5))blockers.push("A work area can contain no more than five client points.");
    if(draft.workAreas.some(area=>area.points.some(point=>!point.trim())))blockers.push("Remove empty client points before approval.");
    if(!draft.deliveryOptions.trim())blockers.push("Add the client-facing delivery options.");
    if(!draft.nextDecision.trim())blockers.push("Add the client-facing next decision.");
    if(!draft.evidenceLimitations.trim())blockers.push("Add the important evidence limitations.");
  }
  return[...new Set(blockers)];
}

export function updateSectionTenWorkAreaSummary(assessment:SectionTenAssessmentV2,areaId:SectionTenWorkAreaId,summary:string):SectionTenAssessmentV2{
  return{...assessment,workAreas:assessment.workAreas.map(area=>area.id===areaId?{...area,summary,summaryEditedBySteve:true}:area),updatedAt:now(),updatedBy:"Demo Operator"};
}

export function regenerateSectionTenWorkAreaSummary(assessment:SectionTenAssessmentV2,areaId:SectionTenWorkAreaId):SectionTenAssessmentV2{
  const template=SECTION_TEN_WORK_AREAS.find(area=>area.id===areaId)!;
  return{...assessment,workAreas:assessment.workAreas.map(area=>area.id===areaId?{...area,summary:template.defaultSummary,summaryEditedBySteve:false}:area),updatedAt:now(),updatedBy:"Demo Operator"};
}
